import { AffectedEntityType, EventState } from '@bp/planung-graphql-types';
import { extractClassGroupNamesFromEvent } from '@bp/shared-planung-utils';
import {
  AccessIcon,
  ActionBar,
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Card,
  Dropdown,
  DropdownMenu,
  EditIcon,
  EmptyState,
  Grid,
  GridColumn,
  GridRow,
  InfoIcon,
  InvisibleIcon,
  Modal,
  PrintIcon,
  Row,
  SpinnerIcon,
  Sticky,
  Switch,
  TextArea,
  TimetableView,
  UploadIcon,
  useMediaQuery,
  VisibleIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFilter, CustomFilterEntry } from '../../../../components/CustomFilter/CustomFilter';
import { SubstitutionFormRowStatus } from '../../../../components/Substitutions/forms/SubstitutionFormRow';
import { CancelEventModal } from '../../../../components/Substitutions/modals/CancelEventModal';
import { DifferentTimeModal } from '../../../../components/Substitutions/modals/DifferentTimeModal';
import { MergeEventModal } from '../../../../components/Substitutions/modals/MergeEventModal';
import { SubstitutionAnnouncementsModal } from '../../../../components/Substitutions/modals/SubstitutionAnnouncementsModal';
import { SubstitutionModal } from '../../../../components/Substitutions/modals/SubstitutionModal';
import { WorkOrderModal } from '../../../../components/Substitutions/modals/WorkOrderModal';
import { useSubstitutionStore } from '../../../../components/Substitutions/SubstitutionProvider';
import {
  SubstitutionEventsTable,
  SubstitutionEventsTableType,
} from '../../../../components/Substitutions/tables/SubstitutionEventsTable';
import { useAllTimeGridEntries } from '../../../../components/TimeGrid/graphql/hooks/useTimeGridEntries';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import {
  EventClassOrGroupType,
  SubstitutionReason,
  use_EventsInRangeQuery,
  use_SchoolEventsQuery,
  use_SetPrintEventMutation,
  use_SubstitutionFilterTimetableVersionsQuery,
  useSubstitutionAnnouncementQuery,
} from '../../../../types/planung-graphql-client-defs';
import { apiHelper } from '../../../../utils/apiHelper';
import { normalizedDate } from '../../../../utils/dateCalculations';
import { getTextForSubstitutionReason } from '../../../../utils/events';
import { toSharedGroupsData } from '../../../../utils/sharedGroupsData';
import {
  downloadSubstitutionInfoReport,
  downloadSubstitutionReport,
  downloadSubstitutionStudentReport,
} from '../../../../utils/substitutionReport';
import { showSuccessToast } from '../../../../utils/toast';
import { isNotEmpty } from '../../../../utils/typeguards';
import styles from './PlanSubstitutions.module.scss';
import {
  assignOverlapGroups,
  createMapFromArray,
  findClassesWithoutEvents,
  findMergeTimetableEvents,
  mapSubstitutionTeachers,
  mapTeachers,
  parseState,
  SubstitutionDataEntry,
  SubstitutionDataMissingTeacherEntry,
  SubstitutionTimetableEntry,
} from './utils';

export type SubstitutionDataGroupEntry = SubstitutionDataEntry & {
  length: number;
  index: number;
};

export type SubstitutionDataHighlightEntry = {
  highlightUuid: string;
  highlightName: string;
  teacherUuid: string;
  teacherName: string;
  status: Extract<SubstitutionFormRowStatus, 'on-school-event' | 'signed-out' | 'attendance'>;
  start: Dayjs;
  end: Dayjs;
  renderStart: Dayjs;
  renderEnd: Dayjs;
  fullDay: boolean;
  inTimegrid: boolean;
  groupClassNames?: string;
};

const NO_CLASSES: SubstitutionDataEntry[] = [];

export const PlanSubstitutions = observer(() => {
  const isMobile = useIsMobile();
  // unter 1440 px ist neben der Tabelle kein Platz für die rechte Spalte (Ankündigungen, Klassen-Stundenplan)
  const isCompact = useMediaQuery('(max-width: 1439.98px)');
  const { t } = useTranslation();
  const perms = usePermissionChecker();
  const userStore = useUserConfigContext();
  const substitutionStore = useSubstitutionStore();
  const currentSchoolYear = userStore.selectedSchoolYear;
  const eventContext = useMemorizedCacheTag('EVENT');
  const schoolEventContext = useMemorizedCacheTag('SCHOOL_EVENT');
  const announcementContext = useMemorizedCacheTag('SUBSTITUTION_ANNOUNCEMENTS');
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims?.getOrganizationUuid();

  /** Farbgebung des Stundenplans, pro Person gespeichert (`scope: 'substitutions'`). */
  const colorMode = userStore.getSettings('substitutions').timetableColorMode;

  const selectedDay = substitutionStore.selectedDay;
  const dateString = substitutionStore.dateString;

  // selected day in school year
  const inSchoolYear = selectedDay.isBetween(
    dayjs(currentSchoolYear?.start),
    dayjs(currentSchoolYear?.end),
    'day',
    '[]',
  );

  const relevantMax = useRef<number>(0);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [announcementsCollapsed, setAnnouncementsCollapsed] = useState<boolean>(true);
  /** Die mit den Pfeilen gewählte Klasse des Stundenplans rechts – gültig nur für die Zeile, zu der `rowKey` gehört. */
  const [classSelection, setClassSelection] = useState<{ rowKey: string; index: number }>({ rowKey: '', index: 0 });
  const [selectedEvent, setSelectedEvent] = useState<SubstitutionEventsTableType | null>(null);
  const [customFilter, setCustomFilter] = useState<CustomFilterEntry[]>([]);

  const [substitutionModalOpen, setSubstitutionModalOpen] = useState<boolean>(false);
  const [timetableOverlayOpen, setTimetableOverlayOpen] = useState<boolean>(false);
  const [cancelEventModalOpen, setCancelEventModalOpen] = useState<boolean>(false);
  const [mergeEventModalOpen, setMergeEventModalOpen] = useState<boolean>(false);
  const [workOrderModalOpen, setWorkOrderModalOpen] = useState<boolean>(false);
  const [differentTimeModalOpen, setDifferentTimeModalOpen] = useState<boolean>(false);
  const [announcementsModalOpen, setAnnouncementsModalOpen] = useState<boolean>(false);

  const teacherEventsRef = useRef<Map<string, SubstitutionEventsTableType[]>>(new Map());

  const selectedDayStartString = selectedDay.startOf('day').utc().toISOString();
  const selectedDayEndString = selectedDay.endOf('day').utc().toISOString();

  const [, updatePrint] = use_SetPrintEventMutation();

  // Stunden aller Zeitraster der Organisation, nach Beginn sortiert.
  const timeGridEntries = useAllTimeGridEntries();

  const firstTimeGridEntry = timeGridEntries?.[0];
  const lastTimeGridEntry = timeGridEntries?.[timeGridEntries.length - 1];

  const timetableStart = firstTimeGridEntry ? dayjs(firstTimeGridEntry.start).startOf('hour') : dayjs().set('hour', 6);

  const timetableEnd = lastTimeGridEntry ? dayjs(lastTimeGridEntry.end).endOf('hour') : dayjs().set('hour', 18);

  // normalize date so that it is compareable
  const normalizedTimetableStart = normalizedDate(timetableStart, selectedDay);
  const normalizedTimetableEnd = normalizedDate(timetableEnd, selectedDay);

  const [{ data: eventData }, refetch] = use_EventsInRangeQuery({
    variables: { start: selectedDayStartString, end: selectedDayEndString },
    context: eventContext,
    pause: !selectedDay,
  });

  const { classesData, subjectData, teacherData, groupsData, divisionsData, roomsData, gradeGroupData } =
    useLoadBasicData({
      pause: false,
    });
  const sharedGroupsData = useMemo(() => toSharedGroupsData(groupsData), [groupsData]);

  const [{ data: eventDataSchoolEvents }] = use_SchoolEventsQuery({
    variables: {
      organizationUuid,
      from: dateString,
      to: dateString,
    },
    context: schoolEventContext,
  });

  const [{ data: announcementsData }] = useSubstitutionAnnouncementQuery({
    variables: { selectedDay: dateString },
    context: announcementContext,
    pause: !selectedDay,
  });

  // aktive Versionen der Stundenpläne, in deren Zeitraum der gewählte Tag liegt
  const [{ data: timetableVersionsData }] = use_SubstitutionFilterTimetableVersionsQuery({
    variables: { date: dateString },
    pause: !selectedDay,
  });

  const publicAnnouncement = announcementsData?.substitutionAnnouncement?.publicAnnouncement ?? '';
  const internalAnnouncement = announcementsData?.substitutionAnnouncement?.internalAnnouncement ?? '';

  const lookupMaps = useMemo(() => {
    return {
      classMap: createMapFromArray(classesData?.classes ?? []),
      teacherMap: createMapFromArray(teacherData?.people ?? []),
      subjectMap: createMapFromArray(subjectData?.subjects ?? []),
      groupMap: createMapFromArray(groupsData?.groups ?? []),
      divisionMap: createMapFromArray(divisionsData?.divisions ?? []),
      roomMap: createMapFromArray(roomsData?.rooms ?? []),
    };
  }, [
    classesData?.classes,
    teacherData?.people,
    subjectData?.subjects,
    groupsData?.groups,
    divisionsData?.divisions,
    roomsData?.rooms,
  ]);

  const events: SubstitutionEventsTableType[] = useMemo(() => {
    if (!eventData?.eventsInRange || !lookupMaps) return [];

    const { subjectMap, teacherMap, classMap, groupMap, divisionMap, roomMap } = lookupMaps;

    teacherEventsRef.current.clear();

    const mappedEvents: SubstitutionEventsTableType[] = eventData.eventsInRange.map((event) => {
      const classesOrGroups = event.isSchoolEvent ? event.classesOrGroups : event.originalClassesOrGroups;
      const hasClassesOrGroups = event.classesOrGroups.length === 0;
      const subject = subjectMap.get(event.subjectUuid ?? '');
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);

      const aggregated = event.aggregatedIntoUuid
        ? eventData.eventsInRange.find((e) => e.uuid === event.aggregatedIntoUuid)
        : null;

      const teachers = mapTeachers(aggregated?.persons || event.persons, teacherMap, hasClassesOrGroups);

      const substitutionTeachers = mapSubstitutionTeachers(event.persons, teacherMap);

      const states = parseState(event.currentState);

      const missingTeachers: SubstitutionDataMissingTeacherEntry[] = states
        .filter((s) => {
          return (
            (s.state == EventState.PersonNotAvailable || s.state == EventState.PersonOnSchoolTrip) &&
            s.affectedEntityType == AffectedEntityType.Person
          );
        })
        .filter((value, index, array) => {
          return array.findIndex((v) => v.affectedEntityUuid === value.affectedEntityUuid) === index;
        })
        .map((s): SubstitutionDataMissingTeacherEntry => {
          const teacher = teacherMap.get(s.affectedEntityUuid as string);
          return {
            uuid: teacher?.uuid ?? '',
            name: teacher?.displayName ?? '',
            shortName: teacher?.shortName ?? '',
            // Der Zustand sagt, warum die Lehrkraft fehlt; affectedEntityType ist hier immer die Person.
            status: s.state === EventState.PersonOnSchoolTrip ? 'on-school-event' : 'signed-out',
            fullDay: s.fullDay ?? false,
            state: s.state,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      const { groupClassNamesLong, groupClassNamesShort } = extractClassGroupNamesFromEvent({
        eventClassesOrGroups: classesOrGroups,
        data: { classesData: { classes: classesData.classes }, groupsData: sharedGroupsData, divisionsData },
      });

      const { groupClassNamesLong: originalGroupClassNamesLong, groupClassNamesShort: originalGroupClassNamesShort } =
        extractClassGroupNamesFromEvent({
          eventClassesOrGroups: event.originalClassesOrGroups,
          data: { classesData: { classes: classesData.classes }, groupsData: sharedGroupsData, divisionsData },
        });

      let aggregatedGroupClassNamesLong = groupClassNamesLong;
      let aggregatedGroupClassNamesShort = groupClassNamesShort;
      let aggregatedOriginalGroupClassNamesLong = null;
      let aggregatedOriginalGroupClassNamesShort = null;

      if (aggregated) {
        const aggregatedResult = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: aggregated.classesOrGroups,
          data: { classesData: { classes: classesData.classes }, groupsData: sharedGroupsData, divisionsData },
        });
        aggregatedGroupClassNamesLong = aggregatedResult.groupClassNamesLong;
        aggregatedGroupClassNamesShort = aggregatedResult.groupClassNamesShort;

        const aggregatedOriginalResult = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: aggregated.originalClassesOrGroups,
          data: { classesData: { classes: classesData.classes }, groupsData: sharedGroupsData, divisionsData },
        });
        aggregatedOriginalGroupClassNamesLong = aggregatedOriginalResult.groupClassNamesLong;
        aggregatedOriginalGroupClassNamesShort = aggregatedOriginalResult.groupClassNamesShort;
      }

      const allOriginalClasses = new Map<string, SubstitutionDataEntry>();

      classesOrGroups
        .filter((cg) => cg.type === EventClassOrGroupType.Class)
        .forEach((c) => {
          const classData = classMap.get(c.uuid);
          if (classData && classData.uuid && classData.name && classData.shortName) {
            allOriginalClasses.set(c.uuid, {
              uuid: classData.uuid,
              name: classData.name,
              shortName: classData.shortName,
            });
          }
        });

      classesOrGroups
        .filter((cg) => cg.type === EventClassOrGroupType.Group)
        .forEach((g) => {
          const group = groupMap.get(g.uuid);
          const division = group ? divisionMap.get(group.divisionUuid) : undefined;
          if (division?.class && division.class.uuid && division.class.name && division.class.shortName) {
            allOriginalClasses.set(division.class.uuid, {
              uuid: division.class.uuid,
              name: division.class.name,
              shortName: division.class.shortName,
            });
          }
        });

      const classGroupUuidsOnSchoolTrip = states
        .filter((da) => da.state === EventState.ClassOnSchoolTrip || da.state === EventState.GroupOnSchoolTrip)
        .map((da) => da.affectedEntityUuid);

      const allClassesAndGroupsOnSchoolTrip = event.originalClassesOrGroups.every((c) => {
        return classGroupUuidsOnSchoolTrip.includes(c.uuid);
      });

      const doNotSubstitute = states.some((s) => {
        return s.state === EventState.NeverSubstituted;
      });

      const perparedEvent: SubstitutionEventsTableType = {
        uuid: event.uuid,
        onSchoolTrip: allClassesAndGroupsOnSchoolTrip,
        doNotSubstitute: doNotSubstitute,
        output: event.print,
        start: eventStart,
        end: eventEnd,
        involvedClasses: Array.from(allOriginalClasses.values()),
        groupClassNames: aggregatedGroupClassNamesShort,
        originalGroupClassNames: originalGroupClassNamesShort,
        groupClassNamesLong: aggregatedGroupClassNamesLong,
        originalGroupClassNamesLong: originalGroupClassNamesLong,
        aggregatedEventOriginalGroupClassNames: aggregatedOriginalGroupClassNamesShort,
        aggregatedEventOriginalGroupClassNamesLong: aggregatedOriginalGroupClassNamesLong,
        classes: classesOrGroups
          .filter((cg) => cg.type === EventClassOrGroupType.Class)
          .map((c) => {
            const classData = classMap.get(c.uuid);
            return {
              uuid: classData?.uuid ?? '',
              name: classData?.name ?? '',
              shortName: classData?.shortName ?? '',
            };
          }),
        groups: classesOrGroups
          .filter((cg) => cg.type === EventClassOrGroupType.Group)
          .map((g) => {
            const group = groupMap.get(g.uuid);
            const division = group ? divisionMap.get(group.divisionUuid) : null;

            return {
              uuid: group?.uuid ?? '',
              name: `${group?.name ?? ''} (${division?.class?.name ?? ''})`,
              shortName: `${group?.shortName ?? ''} (${division?.class?.shortName ?? ''})`,
              length: division?.groups.length ?? 1,
              index: group?.order ?? 0,
            };
          })
          .sort(),
        subject: {
          uuid: subject?.uuid ?? '',
          name: subject?.name ?? '',
          shortName: subject?.shortName ?? '',
        },
        rooms: (aggregated ?? event).roomUuids.map((uuid) => {
          const room = roomMap.get(uuid);
          return {
            uuid: room?.uuid ?? '',
            name: room?.name ?? '',
            shortName: room?.name ?? '',
          };
        }),
        originalRooms: event.originalRooms.map((r) => ({
          uuid: r.uuid,
          name: r.name,
          shortName: '',
        })),
        teachers,
        originalTeachers: event.originalPersonUuids.map((uuid) => {
          const t = teacherMap.get(uuid);
          return {
            name: t?.displayName ?? '',
            uuid: t?.uuid ?? '',
            shortName: t?.shortName ?? '',
          };
        }),
        missingTeachers,
        substitutionTeachers,
        action: event.substitutionReasons ? getTextForSubstitutionReason(event.substitutionReasons) : '',
        comment: event.isSchoolEvent ? (event.comment ?? '') : (event.substitutionComment ?? ''),
        conflicts: JSON.parse(event.currentState ?? '[]'),
        isSchoolEvent: event.isSchoolEvent,
        substitutionReason: event.substitutionReasons ?? null,
        substitutionComment: event.substitutionComment ?? null,
        isCancelled: event.canceled || event.substitutionReasons === SubstitutionReason.Cancelled,
        timeGridEntryShortName: event.timeGridEntryShortName ?? null,
      };

      const eventTeachers = new Set<string>();
      teachers.forEach((t) => eventTeachers.add(t.uuid));
      substitutionTeachers.forEach((t) => eventTeachers.add(t.uuid));
      eventTeachers.forEach((teacherUuid) => {
        if (!teacherEventsRef.current.has(teacherUuid)) {
          teacherEventsRef.current.set(teacherUuid, []);
        }
        teacherEventsRef.current.get(teacherUuid)?.push({
          ...perparedEvent,
        });
      });
      return perparedEvent;
    });

    const groupedByStart = mappedEvents.reduce(
      (acc, event) => {
        const start = event.start.toISOString();
        if (!acc[start]) {
          acc[start] = [];
        }
        acc[start].push(event);
        return acc;
      },
      {} as Record<string, SubstitutionEventsTableType[]>,
    );

    const sortedEvents = Object.values(groupedByStart).map((group) =>
      group.sort((a, b) => {
        const aClass = a.groupClassNames || a.originalGroupClassNames || '';
        const bClass = b.groupClassNames || b.originalGroupClassNames || '';
        return aClass.localeCompare(bClass, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }),
    );

    const schoolEvents: SubstitutionEventsTableType[] =
      eventDataSchoolEvents?.schoolEvents.map((event) => {
        const teachers = event.persons.map((p) => {
          const teacher = teacherMap.get(p.uuid);
          return {
            uuid: teacher?.uuid ?? '',
            name: teacher?.displayName ?? p.listName,
            shortName: teacher?.shortName ?? '',
            status: 'normal' as const,
            givenSubstituteHours: null,
          };
        });

        const rooms = event.rooms.map((room) => {
          return {
            uuid: room?.uuid ?? '',
            name: room?.name ?? '',
            shortName: room?.name ?? '',
          };
        });

        const a: SubstitutionEventsTableType = {
          uuid: event.uuid,
          start: dayjs(event.start),
          output: true,
          end: dayjs(event.end),
          involvedClasses: [],
          groupClassNames: event.groupClassNames,
          originalGroupClassNames: event.groupClassNames,
          groupClassNamesLong: event.groupClassNamesLong,
          originalGroupClassNamesLong: event.groupClassNamesLong,
          classes: [],
          groups: [],
          subject: event.subject,
          rooms: rooms,
          teachers: teachers,
          missingTeachers: [],
          substitutionTeachers: [],
          action: '',
          comment: event.comment ?? '',
          conflicts: [],
          isSchoolEvent: true,
          substitutionReason: null,
          substitutionComment: null,
          isCancelled: false,
          aggregatedEventOriginalGroupClassNames: null,
          aggregatedEventOriginalGroupClassNamesLong: null,
          originalTeachers: teachers,
          originalRooms: rooms,
          onSchoolTrip: event.isClassTrip,
          doNotSubstitute: false,
          timeGridEntryShortName: null,
        };
        return a;
      }) ?? [];

    return [...schoolEvents, ...sortedEvents.flat()];
  }, [
    eventDataSchoolEvents?.schoolEvents,
    eventData?.eventsInRange,
    classesData.classes,
    sharedGroupsData,
    divisionsData,
    lookupMaps,
  ]);

  const customFilterMaps = useMemo(() => {
    const gradeGroupFilterUuids =
      customFilter.find((c) => c.typeId === 'gradeGroups')?.values?.map((g) => g.value as string) ?? [];
    const timetableFilterUuids =
      customFilter.find((c) => c.typeId === 'timetables')?.values?.map((t) => t.value as string) ?? [];

    const timetableVersions =
      timetableVersionsData?.activeTimetableVersionsForDate.filter((version) =>
        timetableFilterUuids.includes(version.timetableUuid),
      ) ?? [];

    const versionClassesUuids = timetableVersions.flatMap((v) => v.classUuids);

    return { gradeGroupFilterUuids, versionClassesUuids };
  }, [customFilter, timetableVersionsData?.activeTimetableVersionsForDate]);

  const filteredEvents: SubstitutionEventsTableType[] = useMemo(() => {
    if (!events.length) return [];

    const { gradeGroupFilterUuids, versionClassesUuids } = customFilterMaps;

    const customFiltered = events.filter((e) => {
      if (gradeGroupFilterUuids.length > 0) {
        const classesGradeGroupUuids = e.classes
          .map((c) => classesData?.classes.find((cls) => cls.uuid === c.uuid)?.gradeGroup?.uuid)
          .filter(Boolean);

        const hasGradeGroup = classesGradeGroupUuids.some((uuid) => uuid && gradeGroupFilterUuids.includes(uuid));
        if (!hasGradeGroup) return false;
      }

      if (versionClassesUuids.length > 0) {
        const classesUuids = e.involvedClasses.map((i) => i.uuid);
        const isInTimetable = classesUuids.some((uuid) => versionClassesUuids.includes(uuid));
        if (!isInTimetable) return false;
      }

      return true;
    });

    const relevantFiltered = customFiltered.filter((e) => {
      // Schulveranstaltungen sind immer relevant
      if (e.isSchoolEvent) return true;

      // Events auf Schulfahrt sind nicht relevant
      if (e.onSchoolTrip || e.doNotSubstitute) return false;

      // Prüfe ob Event relevant ist
      const hasOutput = e.output;
      const hasSubstitutionReason = e.substitutionReason;
      const hasPersonConflicts = e.conflicts.some(
        (s) => s.state.includes(EventState.PersonNotAvailable) || s.state.includes(EventState.PersonMissing),
      );

      return hasOutput || hasSubstitutionReason || hasPersonConflicts;
    });

    return showAll ? customFiltered : relevantFiltered;
  }, [events, showAll, customFilterMaps, classesData?.classes]);

  relevantMax.current = events.filter((e) => {
    // Schulveranstaltungen sind immer relevant
    if (e.isSchoolEvent) return true;

    // Events auf Schulfahrt sind nicht relevant
    if (e.onSchoolTrip || e.doNotSubstitute) return false;

    // Prüfe ob Event relevant ist
    const hasOutput = e.output;
    const hasSubstitutionReason = e.substitutionReason;
    const hasPersonConflicts = e.conflicts.some(
      (s) => s.state.includes(EventState.PersonNotAvailable) || s.state.includes(EventState.PersonMissing),
    );

    return hasOutput || hasSubstitutionReason || hasPersonConflicts;
  }).length;

  // Die markierte Zeile: der gewählte Termin, solange er in der Tabelle steht, sonst die erste Zeile. Die Tabelle kennt
  // Zeilen nur über ihre Position in `filteredEvents` – sie wird hier aus der uuid abgeleitet.
  const highlightedEvent =
    filteredEvents.find((e) => e.uuid === substitutionStore.selectedEventUuid) ?? filteredEvents[0] ?? null;
  const highlightedRowId = highlightedEvent ? String(filteredEvents.indexOf(highlightedEvent)) : null;

  // Der Klassen-Stundenplan rechts zeigt die Klassen der markierten Zeile – auch nach dem Neuladen oder Umschalten der
  // Ansicht. Eine andere Zeile beginnt wieder bei ihrer ersten Klasse.
  const selectedClassEntries: SubstitutionDataEntry[] = highlightedEvent?.involvedClasses ?? NO_CLASSES;
  const highlightedRowKey = `${highlightedEvent?.uuid ?? ''}:${selectedClassEntries.map((c) => c.uuid).join(',')}`;
  const selectedClassIndex = classSelection.rowKey === highlightedRowKey ? classSelection.index : 0;

  // Zusammenlegen nur anbieten, wenn es einen Partner gibt: eine andere Stunde derselben Klasse, die sich zeitlich
  // überschneidet und in derselben Stunde beginnt (gleiche Regel wie die Auswahl im MergeEventForm)
  const mergeableEventUuids = useMemo(() => {
    const active = events.filter(
      (e) =>
        e.substitutionReason !== SubstitutionReason.Cancelled && e.substitutionReason !== SubstitutionReason.Aggregated,
    );
    const result = new Set<string>();
    for (const event of active) {
      const classUuids = new Set(event.involvedClasses.map((c) => c.uuid));
      const hasPartner = active.some(
        (other) =>
          other.uuid !== event.uuid &&
          other.involvedClasses.some((c) => classUuids.has(c.uuid)) &&
          other.start.isSame(event.start, 'hour') &&
          other.start.isBefore(event.end) &&
          event.start.isBefore(other.end),
      );
      if (hasPartner) result.add(event.uuid);
    }
    return result;
  }, [events]);

  /** Fach, Lehrkräfte und Farbe einer Stunde für das Stundenplan-Raster; die Klassen bzw. Gruppen setzt der Aufrufer. */
  const toTimetableEntry = useCallback(
    (classEvent: SubstitutionEventsTableType) => {
      const { teacherMap } = lookupMaps;
      const subject = subjectData?.subjects.find((s) => s.uuid === classEvent.subject.uuid);

      // Nach Lehrkraft: alle beteiligten Lehrkräfte als schräger Verlauf, in der
      // Reihenfolge des Events und ohne Dopplungen — dieselbe Optik wie auf den
      // Kärtchen. Lehrkräfte ohne Farbe fallen weg; hat keine eine, bleibt der
      // Eintrag farblos (wie heute ein Fach ohne Farbe).
      const teacherColors = Array.from(new Set(classEvent.teachers.map((teacher) => teacher.uuid)))
        .map((uuid) => teacherMap.get(uuid)?.timetableConfig?.color)
        .filter((color): color is string => isNotEmpty(color));

      const colors = colorMode === 'teacher' ? teacherColors : [subject?.timetableConfig?.color].filter(isNotEmpty);

      return {
        uuid: classEvent.uuid,
        start: classEvent.start.toDate(),
        end: classEvent.end.toDate(),
        bgColors: colors,
        field1: subject?.shortName,
        field4: Array.from(new Set(classEvent.teachers.map((t) => t.shortName))).join(', '),
        tooltip: {
          field1: subject?.name,
          field4: Array.from(new Set(classEvent.teachers.map((t) => t.name))).join(', '),
        },
        rooms: classEvent.rooms,
      };
    },
    [subjectData?.subjects, colorMode, lookupMaps],
  );

  const timetableEntries: SubstitutionTimetableEntry[] = useMemo(() => {
    if (!selectedClassEntries[selectedClassIndex]) return [];

    const entries: SubstitutionTimetableEntry[] = [];
    const classEvents = events.filter(
      (s) =>
        s.involvedClasses.some((c) => c.uuid === selectedClassEntries[selectedClassIndex].uuid) &&
        s.substitutionReason !== SubstitutionReason.Cancelled &&
        s.substitutionReason !== SubstitutionReason.Aggregated,
    );

    for (const classEvent of classEvents) {
      const entry = toTimetableEntry(classEvent);

      if (classEvent.groups.length > 0) {
        classEvent.groups
          .filter((g) => {
            const selectedClass = classesData?.classes.find(
              (c) => c.uuid === selectedClassEntries[selectedClassIndex].uuid,
            );
            return selectedClass && g.shortName.includes(selectedClass.shortName);
          })
          .forEach((group) => {
            entries.push({
              ...entry,
              field2: group.shortName,
              tooltip: {
                ...entry.tooltip,
                field2: group.name,
              },
            });
          });
      } else {
        entries.push({
          ...entry,
          field2: Array.from(new Set(classEvent.classes.map((c) => c.shortName))).join(', '),
          tooltip: {
            ...entry.tooltip,
            field2: Array.from(new Set(classEvent.classes.map((c) => c.name))).join(', '),
          },
        });
      }
    }

    return assignOverlapGroups(entries);
  }, [selectedClassEntries, selectedClassIndex, classesData?.classes, events, toTimetableEntry]);

  // Zusammenlegen: die Stunden der Klassen der gewählten Stunde selbst – nicht die Auswahl des Klassen-Stundenplans
  // rechts, die nach einem Neuladen oder Umschalten der Ansicht zu einer anderen Zeile gehören kann. Ein Eintrag je
  // Stunde, damit jede genau einmal auswählbar ist; Gruppen stehen mit ihrem Kurznamen darin.
  const mergeTimetableEntries: SubstitutionTimetableEntry[] = useMemo(() => {
    if (!selectedEvent) return [];
    const entries = findMergeTimetableEvents(events, selectedEvent).map((classEvent) => {
      const entry = toTimetableEntry(classEvent);
      const parts = classEvent.groups.length > 0 ? classEvent.groups : classEvent.classes;
      return {
        ...entry,
        field2: Array.from(new Set(parts.map((p) => p.shortName))).join(', '),
        tooltip: { ...entry.tooltip, field2: Array.from(new Set(parts.map((p) => p.name))).join(', ') },
      };
    });
    return assignOverlapGroups(entries);
  }, [selectedEvent, events, toTimetableEntry]);

  function handleClassSelection(direction: 'right' | 'left') {
    let newIndex = selectedClassIndex;
    if (direction === 'right') {
      newIndex = newIndex + 1 > selectedClassEntries.length - 1 ? 0 : newIndex + 1;
    } else {
      newIndex = newIndex - 1 < 0 ? selectedClassEntries.length - 1 : newIndex - 1;
    }
    setClassSelection({ rowKey: highlightedRowKey, index: newIndex });
  }

  const selectEvent = useCallback(
    (uuid: string | null) => {
      if (uuid) {
        const event = events.find((s) => s.uuid === uuid);
        setSelectedEvent(event ?? null);
      } else {
        setSelectedEvent(null);
      }
    },
    [events],
  );

  const handleRowSelection = useCallback(
    (row: Row<SubstitutionEventsTableType>) => {
      substitutionStore.setSelectedEventUuid(row.original.uuid);
    },
    [substitutionStore],
  );

  const gradeGroupFilterOptions = useMemo(
    () =>
      gradeGroupData?.gradeGroups.map((gradeGroup) => ({
        value: gradeGroup.uuid,
        label: gradeGroup.name,
      })) ?? [],
    [gradeGroupData?.gradeGroups],
  );

  const timetableFilterOptions: { value: string; label: string }[] = useMemo(
    () =>
      timetableVersionsData?.activeTimetableVersionsForDate.flatMap((version) => {
        return {
          value: version.timetableUuid,
          label: version.timetableName,
        };
      }) ?? [],
    [timetableVersionsData?.activeTimetableVersionsForDate],
  );

  async function handleOutputChange(uuid: string, output: boolean) {
    await updatePrint(
      {
        eventUuid: uuid,
        print: output,
      },
      { context: eventContext },
    );
    refetch({ requestPolicy: 'network-only' });
  }

  function handleSubstitute(uuid: string) {
    selectEvent(uuid);
    setSubstitutionModalOpen(true);
  }

  function handleCancelEvent(uuid: string) {
    selectEvent(uuid);
    setCancelEventModalOpen(true);
  }

  function handleMergeEvent(uuid: string) {
    selectEvent(uuid);
    setMergeEventModalOpen(true);
  }

  function handleWorkOrder(uuid: string) {
    selectEvent(uuid);
    setWorkOrderModalOpen(true);
  }

  function handleDifferentTime(uuid: string) {
    selectEvent(uuid);
    setDifferentTimeModalOpen(true);
  }

  async function publishApp(date: string) {
    setPublishing(true);
    try {
      await apiHelper<void>({
        method: 'POST',
        path: `/app/publish/${date}`,
      });
      showSuccessToast(t('substitutions.published'));
    } finally {
      setPublishing(false);
    }
  }

  const handleFilterChange = useCallback((entry: CustomFilterEntry) => {
    setCustomFilter((prevCustomFilter) => {
      const entries = [...prevCustomFilter];
      const newEntry = entries.find((e) => e.typeId === entry.typeId);
      if (newEntry) newEntry.values = entry.values;
      else entries.push(entry);
      return entries;
    });
  }, []);

  const customFilterEntries = useMemo(
    () => [
      {
        typeId: 'gradeGroups' as const,
        label: t('gradeGroups.title', { count: 2 }),
        options: gradeGroupFilterOptions,
        values: customFilter.find((c) => c.typeId === 'gradeGroups')?.values ?? [],
      },
      {
        typeId: 'timetables' as const,
        label: t('timetable.title', { count: 2 }),
        options: timetableFilterOptions,
        values: customFilter.find((c) => c.typeId === 'timetables')?.values ?? [],
      },
    ],
    [t, gradeGroupFilterOptions, timetableFilterOptions, customFilter],
  );

  if (!inSchoolYear) {
    return (
      <EmptyState
        size='big'
        icon={<AccessIcon />}
        title={t('schoolYear.selectedDayNotInSchoolYear')}
        subtitle={t('schoolYear.selectedValideSchoolYear')}
      />
    );
  }

  const announcementsPanel = (
    <Card
      wrapperClass='mb-4'
      header={{
        headline: t('announcements.title', { count: 2 }),
        actions: [
          {
            callback: () => setAnnouncementsModalOpen(true),
            icon: <EditIcon />,
          },
        ],
      }}
      collapsibleOn='always'
      isCollapsed={announcementsCollapsed}
      onCollapse={() => setAnnouncementsCollapsed(!announcementsCollapsed)}
    >
      <TextArea
        className='mb-2'
        value={publicAnnouncement}
        label={t('announcements.public')}
        name='public'
        rows={6}
        isScrollable
        readonly
      />
      <TextArea
        value={internalAnnouncement}
        label={t('announcements.internal')}
        name='internal'
        rows={6}
        isScrollable
        readonly
      />
    </Card>
  );

  // Klassen-Stundenplan: breiter Desktop rechte Spalte, sonst (< 1440 px) als Overlay über das Zeilen-/Karten-Menü
  const timetablePanel = (
    <Card wrapperClass={styles.timetable}>
      {/* Aus = Fachfarbe (Standard), an = Farbverlauf der beteiligten Lehrkräfte. */}
      <Switch
        className='mb-2'
        name='timetableColorMode'
        label={t('substitutions.colorByTeacher')}
        checked={colorMode === 'teacher'}
        onChange={() =>
          userStore.updateSettings('substitutions', {
            timetableColorMode: colorMode === 'teacher' ? 'subject' : 'teacher',
          })
        }
      />
      <div className={styles['class-selection']}>
        <Button
          hierarchy='tertiary'
          icon={<ArrowLeftIcon />}
          disabled={selectedClassEntries.length <= 1}
          onClick={() => handleClassSelection('left')}
        />
        <div
          className={classNames(styles['selected-class'], {
            [styles.empty]: selectedClassEntries.length === 0,
          })}
        >
          {selectedClassEntries[selectedClassIndex]?.name ?? t('substitutions.noClassSelected')}
        </div>
        <Button
          hierarchy='tertiary'
          icon={<ArrowRightIcon />}
          disabled={selectedClassEntries.length <= 1}
          onClick={() => handleClassSelection('right')}
        />
      </div>
      <TimetableView
        showTimegrid
        start={timetableStart.toDate()}
        end={timetableEnd.toDate()}
        entries={timetableEntries}
        padding='s'
      />
    </Card>
  );

  return (
    <div className={styles['plan-stubstitutions']}>
      <div className={styles.header}>
        <CustomFilter entries={customFilterEntries} onFilterChange={handleFilterChange} />
        <div className={styles.count}>
          {!showAll
            ? t('substitutions.relevantCount', {
                count: filteredEvents.length,
                max: relevantMax.current,
              })
            : t('substitutions.allCount', {
                count: filteredEvents.length,
                max: events.length,
              })}
        </div>
        <ActionBar
          className={styles.bar}
          marginBottom='0'
          onGlobalFilterChange={(value) => setFilter(value)}
          showGlobalFilter={false}
          extendedActionsRight={
            <>
              <Button
                hierarchy='tertiary'
                icon={showAll ? <InvisibleIcon /> : <VisibleIcon />}
                onClick={() => setShowAll(!showAll)}
                className={styles['all-button']}
              >
                {showAll ? t('substitutions.showRelevant') : t('substitutions.showAll')}
              </Button>
              <Button
                hierarchy='tertiary'
                icon={<UploadIcon />}
                onClick={() => publishApp(dateString)}
                disabled={!perms?.canFetchAsVertretungsplanApp() || publishing}
                isLoading={publishing}
              >
                {t('substitutions.publish')}
              </Button>
              <Dropdown
                trigger={
                  <Button hierarchy='tertiary' icon={<PrintIcon />} isLoading={loading}>
                    {t('common.print')}
                  </Button>
                }
              >
                <DropdownMenu
                  data={[
                    {
                      label: t('substitutions.normalReport'),
                      onClick: async () => {
                        setLoading(true);
                        try {
                          await downloadSubstitutionReport(currentSchoolYear?.uuid ?? '', selectedDayStartString);
                        } finally {
                          setLoading(false);
                        }
                      },
                    },
                    {
                      label: t('substitutions.studentReport'),
                      onClick: async () => {
                        setLoading(true);
                        try {
                          await downloadSubstitutionStudentReport(
                            currentSchoolYear?.uuid ?? '',
                            selectedDayStartString,
                          );
                        } finally {
                          setLoading(false);
                        }
                      },
                    },
                    ...(['teachers', 'students'] as const).map((audience) => ({
                      // zwei feste t()-Aufrufe – ein Ausdruck im Argument wird von update:translations nicht erkannt
                      label:
                        audience === 'teachers'
                          ? t('substitutions.infoReportTeachers')
                          : t('substitutions.infoReportStudents'),
                      onClick: async () => {
                        setLoading(true);
                        try {
                          await downloadSubstitutionInfoReport(
                            audience,
                            currentSchoolYear?.uuid ?? '',
                            selectedDayStartString,
                          );
                        } finally {
                          setLoading(false);
                        }
                      },
                    })),
                  ]}
                />
              </Dropdown>
            </>
          }
        />
      </div>

      {/* < 1440 px (nicht mobil): Ankündigungen über der Tabelle, Tabelle in voller Breite */}
      {isCompact && !isMobile && <div className={styles['announcements-top']}>{announcementsPanel}</div>}

      <Grid useFormGap>
        <GridRow>
          <GridColumn width={isCompact && !isMobile ? 12 : 9}>
            <SubstitutionEventsTable
              filter={filter}
              data={filteredEvents}
              selected={highlightedRowId ? { [highlightedRowId]: true } : {}}
              onSubstitute={handleSubstitute}
              onCancelEvent={handleCancelEvent}
              onMergeEvent={handleMergeEvent}
              mergeableEventUuids={mergeableEventUuids}
              onWorkOrder={handleWorkOrder}
              onDifferentTime={handleDifferentTime}
              onRowSelect={handleRowSelection}
              onShowTimetable={
                isCompact
                  ? (row) => {
                      handleRowSelection(row);
                      setTimetableOverlayOpen(true);
                    }
                  : undefined
              }
              onOutputChange={handleOutputChange}
            />
          </GridColumn>
          {(!isCompact || isMobile) && (
            <GridColumn width={3} className={styles['right-col']}>
              {announcementsPanel}
              {/* Header-Höhe (60px) + Abstand: beim Scrollen bleibt der Klassen-Stundenplan im Blick. */}
              {!isCompact && (
                <div className={styles['right-col-sticky-wrapper']}>
                  <Sticky top={76} stickyClassName={styles['right-col-sticky']}>
                    {timetablePanel}
                  </Sticky>
                </div>
              )}
            </GridColumn>
          )}
        </GridRow>
      </Grid>

      {selectedEvent && (
        <SubstitutionModal
          isOpen={substitutionModalOpen}
          timetableStart={normalizedTimetableStart}
          timetableEnd={normalizedTimetableEnd}
          currentEvent={selectedEvent}
          teacherEvents={teacherEventsRef.current}
          onClose={() => {
            setSubstitutionModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <CancelEventModal
          eventUuid={selectedEvent.uuid}
          substitutionComment={selectedEvent.substitutionComment ?? ''}
          isOpen={cancelEventModalOpen}
          onClose={() => {
            setCancelEventModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <MergeEventModal
          isOpen={mergeEventModalOpen}
          originTeachers={selectedEvent.teachers}
          originRooms={selectedEvent.rooms}
          eventStart={selectedEvent.start}
          eventEnd={selectedEvent.end}
          eventUuid={selectedEvent.uuid}
          substitutionComment={selectedEvent.substitutionComment ?? ''}
          timetableStart={timetableStart}
          timetableEnd={timetableEnd}
          timetableEntries={mergeTimetableEntries}
          onClose={() => {
            setMergeEventModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <WorkOrderModal
          eventUuid={selectedEvent.uuid}
          comment={selectedEvent.substitutionComment ?? ''}
          isOpen={workOrderModalOpen}
          originTeachers={selectedEvent.teachers}
          originRooms={selectedEvent.rooms}
          eventStart={selectedEvent.start}
          eventEnd={selectedEvent.end}
          onClose={() => {
            setWorkOrderModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <DifferentTimeModal
          isOpen={differentTimeModalOpen}
          originTeachers={selectedEvent.teachers}
          originRooms={selectedEvent.rooms}
          onClose={() => {
            setDifferentTimeModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {isCompact && (
        <Modal
          width='s'
          title={t('substitutions.showClassTimetable')}
          isOpen={timetableOverlayOpen}
          onRequestClose={() => setTimetableOverlayOpen(false)}
        >
          {timetablePanel}
        </Modal>
      )}

      <SubstitutionAnnouncementsModal
        isOpen={announcementsModalOpen}
        onClose={() => setAnnouncementsModalOpen(false)}
      />
    </div>
  );
});
